/**
 * Probe's Home, as Linear's inbox: the areas on the left, listed under
 * their readiness, and on the right whatever is selected. An area shows its
 * rating and note, the moments its answers fell short, its open actions and
 * its interviews; All areas shows every open action and interview. The area
 * filter is the selection.
 *
 * @module components/apps/probe/home/ProbeHomeSplit
 */
import type { AreaStanding } from "@amoreg/shared/probePractice";
import { areaLabel } from "@amoreg/shared/probePractice";
import { Link } from "@tanstack/react-router";
import { CalendarIcon, CircleIcon, LayersIcon } from "lucide-react";
import { useEffect, useRef, type ReactNode } from "react";

import { cn } from "~/lib/utils";
import {
  PROBE_LIST_LINK,
  PROBE_LIST_ROW,
  ProbeList,
  ProbeProperty,
  QUIET,
  RatingGlyph,
  useProbeCap,
} from "../ProbeParts";
import { areasByStanding, type HomeShow, type HomeView } from "./probeHome";
import {
  ActionRow,
  AreaGlyph,
  AreaRating,
  homeLink,
  InterviewRow,
  lastRehearsed,
  MomentRow,
} from "./ProbeHomeParts";

/** Each readiness group's glyph, as its areas' ratings draw it. */
const STANDING_GLYPH: Readonly<Record<AreaStanding, ReactNode>> = {
  "needs-work": <RatingGlyph level={1} />,
  thin: <RatingGlyph level={2} />,
  strong: <RatingGlyph level={3} />,
  unrehearsed: <CircleIcon aria-hidden className={cn("opacity-50", QUIET)} />,
};

/** A list in a quiet bordered box. */
const BOX = "overflow-hidden rounded-lg border border-border/70";

const PANE_ROW = cn(
  "flex items-start gap-3 text-foreground aria-[current]:bg-foreground/[0.06] aria-[current]:font-medium",
  PROBE_LIST_ROW,
  PROBE_LIST_LINK,
);

export function ProbeHomeSplit({
  view,
  orgSegment,
  show,
}: {
  readonly view: HomeView;
  readonly orgSegment: string;
  readonly show: HomeShow | null;
}) {
  const focused = view.focused;
  const allInterviews = show === "interviews";
  const moments = useProbeCap(view.moments);
  const actions = useProbeCap(view.actions);
  const interviews = useProbeCap(view.interviews, allInterviews);
  const interviewsList = useRef<HTMLElement>(null);
  useEffect(() => {
    if (allInterviews) interviewsList.current?.scrollIntoView({ block: "start" });
  }, [allInterviews]);

  return (
    <div className="@container/home flex min-h-0 flex-1" data-probe-home-layout="split">
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto @3xl/home:flex-row @3xl/home:overflow-hidden">
        <nav
          aria-label="Areas"
          className="flex shrink-0 flex-col divide-y divide-border/70 border-b border-border/70 @3xl/home:w-80 @3xl/home:overflow-y-auto @3xl/home:border-e @3xl/home:border-b-0"
          data-probe-home-areas=""
        >
          <Link
            {...homeLink(orgSegment, null)}
            aria-current={focused === null || undefined}
            className={cn(PANE_ROW, "items-center")}
          >
            <LayersIcon aria-hidden className={cn("size-3.5 w-4 shrink-0", QUIET)} />
            <span className="min-w-0 flex-1">All areas</span>
            <span className={cn("text-xs font-normal tabular-nums", QUIET)}>{view.areas.length}</span>
          </Link>
          {areasByStanding(view.areas).map((group) => (
            <div key={group.standing} role="group" aria-label={group.label}>
              <ProbeList
                id={`probe-home-group-${group.standing}`}
                icon={STANDING_GLYPH[group.standing]}
                title={group.label}
                count={group.areas.length}
                rows={group.areas.map((entry) => (
                  <li key={entry.area}>
                    <Link
                      {...homeLink(orgSegment, entry.area)}
                      aria-current={entry.area === focused?.area || undefined}
                      title={entry.latest?.note || undefined}
                      className={cn(PANE_ROW, "ps-10")}
                      data-probe-area={entry.area}
                    >
                      <span className="min-w-0 flex-1 text-pretty">{areaLabel(entry.area)}</span>
                      {entry.latest === null ? null : (
                        <span className={cn("shrink-0 text-xs leading-5 font-normal tabular-nums", QUIET)}>
                          {lastRehearsed(entry)}
                        </span>
                      )}
                    </Link>
                  </li>
                ))}
              />
            </div>
          ))}
        </nav>

        <section
          aria-label={focused === null ? "All areas" : areaLabel(focused.area)}
          className="min-w-0 flex-1 @3xl/home:overflow-y-auto"
          data-probe-home-detail={focused?.area ?? "all"}
        >
          <div className="mx-auto flex w-full max-w-3xl flex-col gap-5 px-3 pt-5 pb-10 sm:px-5">
            {focused !== null ? (
              <header className="flex flex-col gap-1.5">
                <h2 className="flex min-w-0 items-center gap-2.5 text-lg leading-7 font-semibold text-pretty text-foreground">
                  <AreaGlyph entry={focused} />
                  {areaLabel(focused.area)}
                </h2>
                <div className="-ms-2 flex flex-wrap items-center gap-x-1 text-[13px]">
                  <ProbeProperty>
                    <AreaRating entry={focused} />
                  </ProbeProperty>
                  {focused.latest !== null ? (
                    <ProbeProperty icon={<CalendarIcon />} title="Last rehearsed">
                      {lastRehearsed(focused)}
                    </ProbeProperty>
                  ) : null}
                </div>
                {focused.latest !== null && focused.latest.note.length > 0 ? (
                  <p className="text-sm leading-6 text-pretty text-foreground/85">{focused.latest.note}</p>
                ) : null}
              </header>
            ) : null}

            {focused !== null ? (
              <div className={BOX}>
                <ProbeList
                  id="probe-home-moments"
                  title="Moments"
                  count={view.moments.length}
                  more={moments.more}
                  rows={moments.shown.map((moment) => (
                    <MomentRow key={`${moment.interviewId}:${moment.turn}`} moment={moment} orgSegment={orgSegment} />
                  ))}
                />
              </div>
            ) : null}

            <div className={BOX}>
              <ProbeList
                id="probe-home-actions"
                title="Actions"
                count={view.actions.length}
                more={actions.more}
                data-probe-home-actions=""
                rows={actions.shown.map((action) => (
                  <ActionRow key={`${action.interviewId}:${action.text}`} action={action} orgSegment={orgSegment} />
                ))}
              />
            </div>

            <div className={BOX}>
              <ProbeList
                id="probe-home-interviews"
                title="Interviews"
                count={view.interviews.length}
                more={interviews.more}
                listRef={interviewsList}
                data-probe-home-recent=""
                rows={interviews.shown.map((interview) => (
                  <InterviewRow key={interview.id} interview={interview} orgSegment={orgSegment} />
                ))}
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
